import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../App';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Save, X, RotateCcw, Tag, ChevronDown, ChevronUp, AlignLeft, AlignCenter, AlignRight, Bold, Italic, Upload } from 'lucide-react';

// ── Quill config ────────────────────────────────────────────────────────────────
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['clean'],
  ],
};

// ── Placeholder data ─────────────────────────────────────────────────────────────
const DEFAULT_PLACEHOLDERS = [
  { key: '{{FIRMA_ADI}}', label: 'Firma Adı (Kısa)', is_system: true },
  { key: '{{FIRMA_UNVAN}}', label: 'Firma Ünvanı (Tam)', is_system: true },
  { key: '{{FIRMA_ADRES}}', label: 'Firma Adres', is_system: true },
  { key: '{{FIRMA_EMAIL}}', label: 'Firma E-posta', is_system: true },
  { key: '{{FIRMA_POSTA_KODU}}', label: 'Firma Posta Kodu', is_system: true },
  { key: '{{FIRMA_SEHIR}}', label: 'Firma Şehri', is_system: true },
  { key: '{{FIRMA_TELEFON}}', label: 'Firma Telefon', is_system: true },
  { key: '{{FIRMA_ULKE}}', label: 'Firma Ülkesi', is_system: true },
  { key: '{{FIRMA_VERGI_DAIRESI}}', label: 'Firma Vergi Dairesi', is_system: true },
  { key: '{{FIRMA_VERGI_NO}}', label: 'Firma Vergi No', is_system: true },
  { key: '{{YETKILI_ADI}}', label: 'Firma Yetkili Ad Soyad', is_system: true },
  { key: '{{YETKILI_POSTA_KODU}}', label: 'Firma Yetkili Posta Kodu', is_system: true },
  { key: '{{YETKILI_SEHIR}}', label: 'Firma Yetkili Şehri', is_system: true },
  { key: '{{YETKILI_ULKE}}', label: 'Firma Yetkili Ülkesi', is_system: true },
  { key: '{{YETKILI_UNVAN}}', label: 'Firma Yetkili Unvanı', is_system: true },
  { key: '{{ORGANIZATOR_ADI}}', label: 'Organizatör Adı (Kısa)', is_system: true },
  { key: '{{ORGANIZATOR_ADRES}}', label: 'Organizatör Adres', is_system: true },
  { key: '{{ORGANIZATOR_EMAIL}}', label: 'Organizatör E-posta', is_system: true },
  { key: '{{ORGANIZATOR_POSTA_KODU}}', label: 'Organizatör Posta Kodu', is_system: true },
  { key: '{{ORGANIZATOR_SEHIR}}', label: 'Organizatör Şehri', is_system: true },
  { key: '{{ORGANIZATOR_TELEFON}}', label: 'Organizatör Telefon', is_system: true },
  { key: '{{ORGANIZATOR_ULKE}}', label: 'Organizatör Ülkesi', is_system: true },
  { key: '{{ORGANIZATOR_UNVAN}}', label: 'Organizatör Ünvanı (Tam)', is_system: true },
  { key: '{{ORGANIZATOR_VERGI_DAIRESI}}', label: 'Organizatör Vergi Dairesi', is_system: true },
  { key: '{{ORGANIZATOR_VERGI_NO}}', label: 'Organizatör Vergi No', is_system: true },
  { key: '{{ORG_YETKILI_ADI}}', label: 'Org. Yetkili Ad Soyad', is_system: true },
  { key: '{{ORG_YETKILI_EMAIL}}', label: 'Org. Yetkili E-posta', is_system: true },
  { key: '{{ORG_YETKILI_TELEFON}}', label: 'Org. Yetkili Telefon', is_system: true },
  { key: '{{ORG_YETKILI_UNVAN}}', label: 'Org. Yetkili Görevi', is_system: true },
  { key: '{{FUAR_ADI}}', label: 'Fuar Adı', is_system: true },
  { key: '{{FUAR_ULKE}}', label: 'Fuar Ülkesi', is_system: true },
  { key: '{{FUAR_SEHIR}}', label: 'Fuar Şehri', is_system: true },
  { key: '{{FUAR_MERKEZI}}', label: 'Fuar Merkezi', is_system: true },
  { key: '{{FUAR_BASLAMA_TARIHI}}', label: 'Fuar Başlama Tarihi', is_system: true },
  { key: '{{FUAR_BITIS_TARIHI}}', label: 'Fuar Bitiş Tarihi', is_system: true },
  { key: '{{FUAR_TARIHLERI}}', label: 'Fuar Tarihleri (Aralık)', is_system: true },
  { key: '{{FUAR_BIRIM_FIYAT}}', label: 'Fuar Birim Fiyat', is_system: true },
  { key: '{{STAND_ALAN}}', label: 'Stand Alanı (m²)', is_system: true },
  { key: '{{STAND_BOYUT}}', label: 'Stand Boyutu', is_system: true },
  { key: '{{STAND_FIYAT}}', label: 'Stand Fiyatı/m²', is_system: true },
  { key: '{{STAND_NO}}', label: 'Stand No', is_system: true },
  { key: '{{TARIH}}', label: 'Bugünün Tarihi', is_system: true },
  { key: '{{TOPLAM_TUTAR}}', label: 'Toplam Tutar', is_system: true },
];

const PLACEHOLDER_GROUPS = [
  { id: 'firma', label: 'Firma', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-400', match: k => k.startsWith('{{FIRMA_') },
  { id: 'firma_yetk', label: 'Firma Yetkilileri', color: 'bg-indigo-100 text-indigo-700', dotColor: 'bg-indigo-400', match: k => k.startsWith('{{YETKILI_') },
  { id: 'org', label: 'Organizatör', color: 'bg-teal-100 text-teal-700', dotColor: 'bg-teal-400', match: k => k.startsWith('{{ORGANIZATOR_') },
  { id: 'org_yetk', label: 'Org. Yetkilileri', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-400', match: k => k.startsWith('{{ORG_YETKILI_') },
  { id: 'stand', label: 'Stand & Fuar', color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-400', match: k => k.startsWith('{{STAND_') || k.startsWith('{{FUAR_') || k === '{{TARIH}}' || k === '{{TOPLAM_TUTAR}}' },
  { id: 'other', label: 'Diğer', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-400', match: () => true },
];

function groupPlaceholders(list) {
  const buckets = Object.fromEntries(PLACEHOLDER_GROUPS.map(g => [g.id, []]));
  list.forEach(ph => {
    const g = PLACEHOLDER_GROUPS.find(x => x.match(ph.key)) || PLACEHOLDER_GROUPS[PLACEHOLDER_GROUPS.length - 1];
    buckets[g.id].push(ph);
  });
  return PLACEHOLDER_GROUPS.map(g => ({ ...g, items: buckets[g.id] })).filter(g => g.items.length > 0);
}

// ── Default configs ───────────────────────────────────────────────────────────────
const EMPTY_HEADER = {
  enabled: false,
  height: 80,
  bgColor: '#ffffff',
  logo: { url: '', position: 'left', size: 60 },
  text: { content: '', position: 'right', color: '#333333', fontFamily: 'Arial', fontSize: 13, bold: false, italic: false },
};

const EMPTY_FOOTER = {
  enabled: false,
  height: 50,
  bgColor: '#f9fafb',
  text: { content: '', position: 'center', color: '#6b7280', fontFamily: 'Arial', fontSize: 10, bold: false, italic: false },
  pageNumber: { enabled: false, position: 'center', format: 'Sayfa {n}' },
};

const EMPTY_TEMPLATE = { name: '', content: '', header_config: EMPTY_HEADER, footer_config: EMPTY_FOOTER };

const FONTS = ['Arial', 'Times New Roman', 'Georgia', 'Helvetica', 'Courier New', 'Trebuchet MS'];

// ── Header/Footer Preview ─────────────────────────────────────────────────────────
function HeaderFooterPreview({ cfg, isFooter, pageNum = 1 }) {
  if (!cfg?.enabled) return null;
  const zones = ['left', 'center', 'right'];
  return (
    <div style={{ height: cfg.height, backgroundColor: cfg.bgColor, display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0, borderBottom: isFooter ? undefined : '1px solid #e5e7eb', borderTop: isFooter ? '1px solid #e5e7eb' : undefined }}>
      {zones.map(zone => (
        <div key={zone} style={{ flex: 1, display: 'flex', justifyContent: zone === 'left' ? 'flex-start' : zone === 'center' ? 'center' : 'flex-end', alignItems: 'center', gap: 8 }}>
          {cfg.logo?.url && cfg.logo.position === zone && (
            <img src={cfg.logo.url} alt="logo" style={{ height: cfg.logo.size || (cfg.height - 12), width: 'auto', objectFit: 'contain', maxHeight: cfg.height - 8 }} />
          )}
          {cfg.text?.content && cfg.text.position === zone && (
            <span style={{ color: cfg.text.color, fontFamily: cfg.text.fontFamily, fontSize: cfg.text.fontSize, fontWeight: cfg.text.bold ? 'bold' : 'normal', fontStyle: cfg.text.italic ? 'italic' : 'normal', whiteSpace: 'nowrap' }}>
              {cfg.text.content}
            </span>
          )}
          {isFooter && cfg.pageNumber?.enabled && cfg.pageNumber.position === zone && (
            <span style={{ color: cfg.text?.color || '#6b7280', fontFamily: cfg.text?.fontFamily || 'Arial', fontSize: cfg.text?.fontSize || 10 }}>
              {(cfg.pageNumber.format || 'Sayfa {n}').replace('{n}', pageNum)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Position Toggle Buttons ────────────────────────────────────────────────────────
function PosButtons({ value, onChange, disabled = [] }) {
  const opts = [
    { v: 'left', icon: <AlignLeft className="w-3.5 h-3.5" />, label: 'Sol' },
    { v: 'center', icon: <AlignCenter className="w-3.5 h-3.5" />, label: 'Orta' },
    { v: 'right', icon: <AlignRight className="w-3.5 h-3.5" />, label: 'Sağ' },
  ];
  return (
    <div className="flex rounded-md border overflow-hidden">
      {opts.map(o => (
        <button key={o.v} type="button" title={o.label} disabled={disabled.includes(o.v)}
          onClick={() => !disabled.includes(o.v) && onChange(o.v)}
          className={`flex-1 py-1 flex items-center justify-center transition-colors
            ${value === o.v ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}
            ${disabled.includes(o.v) ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}>
          {o.icon}
        </button>
      ))}
    </div>
  );
}

// ── Header/Footer Builder ─────────────────────────────────────────────────────────
function HFBuilder({ cfg, onChange, title }) {
  const [open, setOpen] = useState(false);
  const logoRef = useRef(null);

  const update = (patch) => onChange({ ...cfg, ...patch });
  const updateText = (patch) => onChange({ ...cfg, text: { ...cfg.text, ...patch } });
  const updateLogo = (patch) => onChange({ ...cfg, logo: { ...cfg.logo, ...patch } });
  const updatePageNum = (patch) => onChange({ ...cfg, pageNumber: { ...(cfg.pageNumber || {}), ...patch } });
  const isFooter = title.toLowerCase().includes('footer');

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updateLogo({ url: ev.target.result });
    reader.readAsDataURL(file);
  };

  const logoDisablesPos = cfg.logo?.url ? [cfg.logo.position] : [];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()}>
            <input type="checkbox" className="w-4 h-4 accent-teal-600" checked={cfg.enabled}
              onChange={e => { update({ enabled: e.target.checked }); if (e.target.checked) setOpen(true); }} />
            <span className="text-sm font-semibold text-gray-700">{title}</span>
          </label>
          {cfg.enabled && <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Etkin · {cfg.height}px</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>

      {open && cfg.enabled && (
        <div className="p-4 space-y-4 bg-white">
          {/* Height + BgColor */}
          <div className="flex items-center gap-6">
            <div className="flex-1 space-y-1">
              <label className="text-xs text-gray-500 font-medium">Yükseklik: <strong>{cfg.height}px</strong></label>
              <input type="range" min="40" max="200" value={cfg.height}
                onChange={e => update({ height: Number(e.target.value) })}
                className="w-full h-1.5 accent-teal-600" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-500 font-medium">Arka Plan</label>
              <div className="flex items-center gap-2">
                <input type="color" value={cfg.bgColor} onChange={e => update({ bgColor: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5" />
                <span className="text-xs text-gray-400 font-mono">{cfg.bgColor}</span>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="space-y-2 border-t pt-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Logo</span>
              {cfg.logo?.url && (
                <button type="button" onClick={() => updateLogo({ url: '' })}
                  className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1">
                  <X className="w-3 h-3" /> Kaldır
                </button>
              )}
            </div>
            {!cfg.logo?.url ? (
              <button type="button" onClick={() => logoRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-200 rounded-lg py-3 flex flex-col items-center gap-1 hover:border-teal-400 hover:bg-teal-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-xs text-gray-500">Logo yükle (PNG, JPG, SVG)</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                <img src={cfg.logo.url} alt="logo" style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain' }} />
                <button type="button" onClick={() => logoRef.current?.click()} className="text-xs text-blue-500 hover:underline">Değiştir</button>
              </div>
            )}
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
            {cfg.logo?.url && (
              <>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Logo Konumu</label>
                  <PosButtons value={cfg.logo.position} onChange={v => updateLogo({ position: v })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500 flex items-center justify-between">
                    <span>Logo Boyutu</span>
                    <strong className="font-mono">{cfg.logo.size || 60}px</strong>
                  </label>
                  <input
                    type="range" min="20" max="150"
                    value={cfg.logo.size || 60}
                    onChange={e => updateLogo({ size: Number(e.target.value) })}
                    className="w-full h-1.5 accent-teal-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[9px] text-gray-400">
                    <span>Küçük (20px)</span>
                    <span>Büyük (150px)</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Text */}
          <div className="space-y-2 border-t pt-3">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Metin</span>
            <input type="text" value={cfg.text.content} onChange={e => updateText({ content: e.target.value })}
              placeholder={isFooter ? 'Footer metni...' : 'Header metni...'}
              className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />            {cfg.text.content && (
              <>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Metin Konumu {logoDisablesPos.length > 0 && <span className="text-gray-400">(logo konumu kullanılamaz)</span>}</label>
                  <PosButtons value={cfg.text.position} onChange={v => updateText({ position: v })} disabled={logoDisablesPos} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Font Ailesi</label>
                    <select value={cfg.text.fontFamily} onChange={e => updateText({ fontFamily: e.target.value })}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500">
                      {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Boyut (pt)</label>
                    <input type="number" min="7" max="40" value={cfg.text.fontSize}
                      onChange={e => updateText({ fontSize: Number(e.target.value) })}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Renk</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={cfg.text.color} onChange={e => updateText({ color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer border border-gray-200 p-0.5" />
                      <span className="text-xs font-mono text-gray-400">{cfg.text.color}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Stil</label>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => updateText({ bold: !cfg.text.bold })}
                        className={`w-8 h-8 rounded border flex items-center justify-center font-bold transition-colors ${cfg.text.bold ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                        <Bold className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => updateText({ italic: !cfg.text.italic })}
                        className={`w-8 h-8 rounded border flex items-center justify-center transition-colors ${cfg.text.italic ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                        <Italic className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Preview swatch */}
                  <div className="space-y-1 flex-1">
                    <label className="text-xs text-gray-500">Önizleme</label>
                    <div className="border rounded-md px-2 py-1 text-sm" style={{ color: cfg.text.color, fontFamily: cfg.text.fontFamily, fontSize: cfg.text.fontSize, fontWeight: cfg.text.bold ? 'bold' : 'normal', fontStyle: cfg.text.italic ? 'italic' : 'normal', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cfg.text.content || 'Metin...'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Page Number section - footer only */}
          {isFooter && (
            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Sayfa Numarası</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-3.5 h-3.5 accent-teal-600"
                    checked={cfg.pageNumber?.enabled || false}
                    onChange={e => updatePageNum({ enabled: e.target.checked, position: cfg.pageNumber?.position || 'center', format: cfg.pageNumber?.format || 'Sayfa {n}' })} />
                  <span className="text-xs text-gray-600">Ekle</span>
                </label>
              </div>
              {cfg.pageNumber?.enabled && (
                <div className="space-y-2 pl-1">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Konum</label>
                    <PosButtons value={cfg.pageNumber.position || 'center'}
                      onChange={v => updatePageNum({ position: v })}
                      disabled={cfg.text?.content ? [cfg.text.position] : []} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Format</label>
                    <select value={cfg.pageNumber.format || 'Sayfa {n}'}
                      onChange={e => updatePageNum({ format: e.target.value })}
                      className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500">
                      <option value="Sayfa {n}">Sayfa {'{n}'} → Sayfa 1</option>
                      <option value="{n}">{'{'}{'{n}'}{'}'}→ 1</option>
                      <option value="{n} / {total}">{'{'}{'{n} / {total}'}{'}'}→ 1 / 5</option>
                      <option value="Sayfa {n} / {total}">Sayfa {'{n}'} / {'{total}'} → Sayfa 1 / 5</option>
                    </select>
                  </div>
                  <div className="text-[10px] text-gray-400 bg-gray-50 rounded px-2 py-1">
                    Önizleme: <strong>{(cfg.pageNumber.format || 'Sayfa {n}').replace('{n}', '1').replace('{total}', '3')}</strong>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Dynamic Fields Sidebar ────────────────────────────────────────────────────────
function DynamicFieldsSidebar({ placeholders, onInsert }) {
  const groups = groupPlaceholders(placeholders);
  const [openGroups, setOpenGroups] = useState(() => Object.fromEntries(groups.map(g => [g.id, true])));

  const toggle = (id) => setOpenGroups(s => ({ ...s, [id]: !s[id] }));

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-3 py-2.5 border-b bg-gray-50 shrink-0">
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Dinamik Alanlar</p>
        <p className="text-[10px] text-gray-400 mt-0.5">Tıkla → editöre ekle</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {groups.map(group => (
          <div key={group.id} className="border-b border-gray-100">
            <button type="button" onClick={() => toggle(group.id)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${group.dotColor}`} />
                <span className="text-[11px] font-semibold text-gray-700">{group.label}</span>
                <span className="text-[10px] text-gray-400">({group.items.length})</span>
              </div>
              {openGroups[group.id] ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
            </button>
            {openGroups[group.id] && (
              <div className="px-2 pb-2 space-y-0.5">
                {group.items.map((p, i) => (
                  <button key={i} type="button" onClick={() => onInsert(p.key)}
                    title={p.label}
                    className="w-full text-left px-2 py-1 rounded hover:bg-teal-50 hover:text-teal-700 transition-colors group"
                    data-testid={`sidebar-placeholder-${p.key}`}>
                    <span className="text-[10px] font-mono text-teal-700 group-hover:text-teal-800 block leading-tight truncate">{p.key}</span>
                    <span className="text-[9px] text-gray-400 group-hover:text-gray-500 block leading-tight truncate">{p.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Default ready template ────────────────────────────────────────────────────────
const STANDART_SOZLESME_CONTENT = `<h2 style="text-align:center"><strong>FUAR STANDI KİRALAMA SÖZLEŞMESİ</strong></h2>
<p style="text-align:center">Bu sözleşme, aşağıda bilgileri verilen taraflar arasında imzalanmıştır.</p>
<p><br></p>
<h3><strong>MADDE 1 – TARAFLAR</strong></h3>
<p><strong>1.1 ORGANİZATÖR</strong></p>
<p>Ünvanı : {{ORGANIZATOR_UNVAN}}</p><p>Kısa Adı : {{ORGANIZATOR_ADI}}</p>
<p>Adresi : {{ORGANIZATOR_ADRES}}, {{ORGANIZATOR_SEHIR}} / {{ORGANIZATOR_ULKE}}</p>
<p>Telefon : {{ORGANIZATOR_TELEFON}}</p><p>E-posta : {{ORGANIZATOR_EMAIL}}</p>
<p>Bundan böyle <strong>"ORGANİZATÖR"</strong> olarak anılacaktır.</p><p><br></p>
<p><strong>1.2 KATILIMCI FİRMA</strong></p>
<p>Ünvanı : {{FIRMA_UNVAN}}</p><p>Adresi : {{FIRMA_ADRES}}, {{FIRMA_SEHIR}} / {{FIRMA_ULKE}}</p>
<p>Vergi Dairesi : {{FIRMA_VERGI_DAIRESI}}</p><p>Vergi No : {{FIRMA_VERGI_NO}}</p>
<p>Telefon : {{FIRMA_TELEFON}}</p><p>E-posta : {{FIRMA_EMAIL}}</p>
<p>Bundan böyle <strong>"KATILIMCI"</strong> olarak anılacaktır.</p><p><br></p>
<h3><strong>MADDE 2 – STAND BİLGİLERİ VE ÜCRET</strong></h3>
<p>Stand No : {{STAND_NO}}</p><p>Stand Boyutu : {{STAND_BOYUT}}</p>
<p>Stand Alanı : {{STAND_ALAN}}</p><p>Birim Fiyat : {{STAND_FIYAT}}</p>
<p><strong>Toplam Tutar : {{TOPLAM_TUTAR}}</strong></p><p><br></p>
<h3><strong>MADDE 3 – YÜRÜRLÜK VE İMZA</strong></h3>
<p>İşbu sözleşme {{TARIH}} tarihinde imzalanmıştır.</p><p><br></p>
<table style="width:100%"><tbody><tr>
  <td style="width:50%;padding:8px;border:1px solid #ccc;text-align:center"><p><strong>ORGANİZATÖR</strong></p><p>{{ORGANIZATOR_ADI}}</p><p><br></p><p>İmza :</p></td>
  <td style="width:50%;padding:8px;border:1px solid #ccc;text-align:center"><p><strong>KATILIMCI</strong></p><p>{{FIRMA_ADI}}</p><p><br></p><p>İmza :</p></td>
</tr></tbody></table>`.trim();

// ── Main component ────────────────────────────────────────────────────────────────
export default function ContractTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(EMPTY_TEMPLATE);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quillRef, setQuillRef] = useState(null);

  const [placeholders, setPlaceholders] = useState(DEFAULT_PLACEHOLDERS);
  const [editPlaceholders, setEditPlaceholders] = useState(null);
  const [savingPlaceholders, setSavingPlaceholders] = useState(false);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    fetchTemplates();
    fetchSettings();
  }, []); // eslint-disable-line

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contract-templates`, { headers: { Authorization: `Bearer ${token()}` } });
      setTemplates(res.data);
    } catch { toast.error('Şablonlar yüklenemedi'); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/contract-settings`, { headers: { Authorization: `Bearer ${token()}` } });
      if (res.data.placeholders?.length) setPlaceholders(res.data.placeholders);
    } catch { /* use defaults */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error('Şablon adı gerekli');
    if (!formData.content.trim() || formData.content === '<p><br></p>') return toast.error('Şablon içeriği gerekli');
    const payload = { name: formData.name, content: formData.content, header_config: formData.header_config, footer_config: formData.footer_config };
    try {
      if (editingTemplate) {
        await axios.put(`${API}/contract-templates/${editingTemplate.id}`, payload, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success('Şablon güncellendi');
      } else {
        await axios.post(`${API}/contract-templates`, payload, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success('Şablon oluşturuldu');
      }
      setFormOpen(false);
      setEditingTemplate(null);
      setFormData(EMPTY_TEMPLATE);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/contract-templates/${deleteTarget.id}`, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Şablon silindi');
      setDeleteTarget(null);
      fetchTemplates();
    } catch { toast.error('Silinemedi'); }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setFormData(EMPTY_TEMPLATE);
    setFormOpen(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setFormData({
      name: t.name,
      content: t.content,
      header_config: t.header_config ? { ...EMPTY_HEADER, ...t.header_config } : { ...EMPTY_HEADER },
      footer_config: t.footer_config ? { ...EMPTY_FOOTER, ...t.footer_config } : { ...EMPTY_FOOTER },
    });
    setFormOpen(true);
  };

  const openStandartTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: 'Standart Stand Kiralama Sözleşmesi', content: STANDART_SOZLESME_CONTENT, header_config: { ...EMPTY_HEADER }, footer_config: { ...EMPTY_FOOTER } });
    setFormOpen(true);
  };

  const insertPlaceholder = (key) => {
    if (quillRef) {
      const editor = quillRef.getEditor();
      const range = editor.getSelection(true);
      editor.insertText(range.index, key);
    }
  };

  // Placeholder management
  const startEditPlaceholders = () => setEditPlaceholders(placeholders.map(p => ({ ...p })));
  const cancelEditPlaceholders = () => setEditPlaceholders(null);
  const updateEditPlaceholder = (idx, field, value) => {
    const updated = [...editPlaceholders];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditPlaceholders(updated);
  };
  const addPlaceholder = () => setEditPlaceholders([...editPlaceholders, { key: '{{YENI_ALAN}}', label: 'Yeni Alan', is_system: false }]);
  const removePlaceholder = (idx) => setEditPlaceholders(editPlaceholders.filter((_, i) => i !== idx));
  const resetToDefaults = () => setEditPlaceholders(DEFAULT_PLACEHOLDERS.map(p => ({ ...p })));
  const savePlaceholders = async () => {
    const keys = editPlaceholders.map(p => p.key.trim());
    if (keys.some(k => !k || !k.startsWith('{{') || !k.endsWith('}}'))) return toast.error('Tüm alan anahtarları {{BÜYÜK_HARF}} formatında olmalı');
    if (new Set(keys).size !== keys.length) return toast.error('Alan anahtarları benzersiz olmalı');
    const sorted = [...editPlaceholders].sort((a, b) => a.label.localeCompare(b.label, 'tr', { sensitivity: 'base' }));
    setSavingPlaceholders(true);
    try {
      await axios.put(`${API}/contract-settings`, { placeholders: sorted }, { headers: { Authorization: `Bearer ${token()}` } });
      setPlaceholders(sorted);
      setEditPlaceholders(null);
      toast.success('Dinamik alanlar kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSavingPlaceholders(false); }
  };

  const handleSaveHeaderFooter = async () => {
    if (!editingTemplate) {
      toast.info('Header/Footer kaydetmek için önce şablonu oluşturun.');
      return;
    }
    try {
      await axios.put(
        `${API}/contract-templates/${editingTemplate.id}`,
        { name: formData.name, content: formData.content, header_config: formData.header_config, footer_config: formData.footer_config },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success('Header/Footer ayarları kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  const closeForm = () => { setFormOpen(false); setEditingTemplate(null); setFormData(EMPTY_TEMPLATE); };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="templates">
        <TabsList className="grid grid-cols-2 w-full max-w-xs">
          <TabsTrigger value="templates" className="gap-1.5"><FileText className="w-4 h-4" /> Şablonlar</TabsTrigger>
          <TabsTrigger value="placeholders" className="gap-1.5" data-testid="tab-placeholders"><Tag className="w-4 h-4" /> Dinamik Alanlar</TabsTrigger>
        </TabsList>

        {/* ── Şablonlar Tab ── */}
        <TabsContent value="templates" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Sözleşme şablonları oluşturun ve yönetin.</p>
            <div className="flex gap-2">
              <Button onClick={openStandartTemplate} size="sm" variant="outline" className="gap-1.5 text-teal-700 border-teal-300 hover:bg-teal-50" data-testid="standart-template-btn">
                <FileText className="w-4 h-4" /> Hazır Şablon
              </Button>
              <Button onClick={openCreate} size="sm" className="gap-1.5" data-testid="new-template-btn">
                <Plus className="w-4 h-4" /> Yeni Şablon
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-6 text-gray-400 text-sm">Yükleniyor...</div>
          ) : templates.length === 0 ? (
            <Card><CardContent className="py-10 text-center">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-400 text-sm">Henüz şablon yok</p>
              <div className="flex gap-2 justify-center mt-3">
                <Button variant="outline" onClick={openStandartTemplate} className="gap-1.5 text-teal-700 border-teal-300 hover:bg-teal-50"><FileText className="w-4 h-4" /> Hazır Şablonla Başla</Button>
                <Button variant="outline" onClick={openCreate} className="gap-1.5"><Plus className="w-4 h-4" /> Boş Şablon</Button>
              </div>
            </CardContent></Card>
          ) : (
            <div className="grid gap-2.5">
              {templates.map(t => (
                <Card key={t.id} data-testid={`template-card-${t.id}`} className="hover:shadow-sm transition-shadow">
                  <CardContent className="py-3 px-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(t.updated_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Dinamik Alanlar Tab ── */}
        <TabsContent value="placeholders" className="mt-4 space-y-4">
          {editPlaceholders === null ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700 font-medium">Şablonlarda kullanılabilecek dinamik alanlar</p>
                  <p className="text-xs text-gray-400 mt-0.5">Şablona eklemek için alan adına tıklayın veya kopyalayın.</p>
                </div>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={startEditPlaceholders} data-testid="edit-placeholders-btn">
                  <Edit className="w-4 h-4" /> Düzenle
                </Button>
              </div>
              <div className="space-y-3">
                {groupPlaceholders(placeholders).map(group => (
                  <div key={group.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className={`px-4 py-2.5 flex items-center gap-2 border-b ${group.color}`}>
                      <span className={`w-2 h-2 rounded-full ${group.dotColor}`} />
                      <span className="text-xs font-bold uppercase tracking-wide">{group.label}</span>
                      <span className="ml-auto text-xs opacity-60">{group.items.length} alan</span>
                    </div>
                    {group.items.map((p, i) => (
                      <div key={i} className="px-4 py-2 grid grid-cols-[1fr_1fr_60px] gap-2 items-center border-t border-gray-100 hover:bg-gray-50 first:border-t-0">
                        <button onClick={() => { navigator.clipboard.writeText(p.key); toast.success(`${p.key} kopyalandı`); }}
                          className="font-mono text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded w-fit hover:bg-teal-100 transition-colors text-left" title="Kopyala" data-testid={`placeholder-btn-${p.key}`}>{p.key}</button>
                        <span className="text-sm text-gray-600">{p.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${p.is_system ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>{p.is_system ? 'Sistem' : 'Özel'}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Dinamik Alanları Düzenle</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="gap-1 text-gray-500" onClick={resetToDefaults}><RotateCcw className="w-3.5 h-3.5" /> Sıfırla</Button>
                  <Button variant="outline" size="sm" className="gap-1" onClick={cancelEditPlaceholders}><X className="w-3.5 h-3.5" /> İptal</Button>
                  <Button size="sm" className="gap-1" onClick={savePlaceholders} disabled={savingPlaceholders} data-testid="save-placeholders-btn"><Save className="w-3.5 h-3.5" /> {savingPlaceholders ? 'Kaydediliyor...' : 'Kaydet'}</Button>
                </div>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_1fr_80px_36px] gap-2 px-2 text-xs font-semibold text-gray-500">
                  <span>Alan Anahtarı</span><span>Açıklama</span><span>Tür</span><span></span>
                </div>
                {editPlaceholders.map((p, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_80px_36px] gap-2 items-center" data-testid={`placeholder-row-${i}`}>
                    <Input value={p.key} onChange={e => updateEditPlaceholder(i, 'key', e.target.value)} placeholder="{{ALAN_ADI}}" className="font-mono text-xs h-8" />
                    <Input value={p.label} onChange={e => updateEditPlaceholder(i, 'label', e.target.value)} placeholder="Alan açıklaması" className="text-sm h-8" />
                    <span className={`text-xs px-2 py-1 rounded-full text-center ${p.is_system ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>{p.is_system ? 'Sistem' : 'Özel'}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => removePlaceholder(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="gap-1.5 w-full mt-2 border-dashed" onClick={addPlaceholder} data-testid="add-placeholder-btn"><Plus className="w-4 h-4" /> Yeni Alan Ekle</Button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                <strong>Not:</strong> Sistem alanları otomatik doldurulur. Özel alanlar şablona eklenir ama içerikleri otomatik doldurulmaz.
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Template Editor Dialog (Full-screen 2-col layout) ── */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: '95vw', maxWidth: 1380, height: '94vh' }}>
            {/* Top bar */}
            <div className="px-5 py-3.5 border-b flex items-center justify-between shrink-0">
              <h2 className="text-base font-semibold text-gray-800">{editingTemplate ? 'Şablonu Düzenle' : 'Yeni Şablon Oluştur'}</h2>
              <button type="button" onClick={closeForm} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"><X className="w-5 h-5" /></button>
            </div>

            {/* Body: left editor + right sidebar */}
            <div className="flex flex-1 min-h-0">

              {/* Left: editor column */}
              <div className="flex-1 overflow-y-auto bg-gray-100 p-5 space-y-4 min-w-0">

                {/* Template name */}
                <div className="bg-white rounded-lg px-4 py-3 shadow-sm space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Şablon Adı *</Label>
                  <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Örn: Standart Katılım Sözleşmesi" className="text-sm" data-testid="template-name-input" />
                </div>

                {/* Header builder */}
                <HFBuilder title="Header (Üst Alan)" cfg={formData.header_config || EMPTY_HEADER}
                  onChange={v => setFormData(p => ({ ...p, header_config: v }))} />

                {/* A4 Page simulation - header/footer on every page */}
                <div className="flex justify-center">
                  <style>{`
                    .a4-editor-wrap,
                    .a4-editor-wrap .ql-container,
                    .a4-editor-wrap .ql-container.ql-snow { background: transparent !important; border: none !important; }
                    .a4-editor-wrap .ql-editor { background: transparent !important; min-height: 300px; padding: 12px 15px; }
                    .a4-editor-wrap .ql-toolbar.ql-snow { background: #f9fafb !important; border: none !important; border-bottom: 1px solid #e5e7eb !important; position: sticky; top: 0; z-index: 10; }
                    .a4-editor-wrap .ql-editor.ql-blank::before { color: #9ca3af; }
                  `}</style>

                  {(() => {
                    const A4_H = 1123;
                    const GAP = 40;
                    const PAGE_SLOT = A4_H; // full page slot height
                    const WHITE_H = A4_H - GAP; // white paper area
                    const MAX_PAGES = 4;
                    const headerCfg = formData.header_config;
                    const footerCfg = formData.footer_config;
                    const headerH = headerCfg?.enabled ? (headerCfg.height || 80) : 0;
                    const footerH = footerCfg?.enabled ? (footerCfg.height || 50) : 0;
                    const totalH = PAGE_SLOT * MAX_PAGES;

                    return (
                      <div className="w-full max-w-[794px] shadow-xl rounded-sm border border-gray-300 overflow-hidden" style={{ background: '#d1d5db', position: 'relative', minHeight: totalH }}>

                        {/* Sticky Quill toolbar needs a static wrapper first */}
                        <div className="a4-editor-wrap" data-testid="template-editor" style={{
                          position: 'relative', zIndex: 1, minHeight: totalH,
                          paddingTop: headerH + 16,
                          paddingBottom: footerH + 16,
                          paddingLeft: 25, paddingRight: 25,
                        }}>
                          <ReactQuill ref={ref => setQuillRef(ref)} theme="snow"
                            value={formData.content}
                            onChange={content => setFormData(p => ({ ...p, content }))}
                            modules={QUILL_MODULES}
                            style={{ fontFamily: 'serif', fontSize: '11pt' }} />
                        </div>

                        {/* Page frames: header + footer + white bg + gap — rendered BEHIND editor (z-index 0) */}
                        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
                          {Array.from({ length: MAX_PAGES }).map((_, i) => (
                            <div key={i} style={{ position: 'absolute', top: i * PAGE_SLOT, left: 0, right: 0, height: PAGE_SLOT }}>
                              {/* White paper area */}
                              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: WHITE_H, background: 'white' }} />
                              {/* Header */}
                              {headerCfg?.enabled && (
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
                                  <HeaderFooterPreview cfg={headerCfg} pageNum={i + 1} />
                                </div>
                              )}
                              {/* Footer */}
                              {footerCfg?.enabled && (
                                <div style={{ position: 'absolute', top: WHITE_H - footerH, left: 0, right: 0, zIndex: 1 }}>
                                  <HeaderFooterPreview cfg={footerCfg} isFooter pageNum={i + 1} />
                                </div>
                              )}
                              {/* Gray gap between pages */}
                              <div style={{ position: 'absolute', top: WHITE_H, left: 0, right: 0, height: GAP, background: '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 10, zIndex: 2 }}>
                                {i < MAX_PAGES - 1 && (
                                  <span style={{ fontSize: 9, color: '#9ca3af', userSelect: 'none' }}>— Sayfa {i + 2} —</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Footer builder */}
                <HFBuilder title="Footer (Alt Alan)" cfg={formData.footer_config || EMPTY_FOOTER}
                  onChange={v => setFormData(p => ({ ...p, footer_config: v }))} />
              </div>

              {/* Right: dynamic fields sidebar */}
              <div className="w-[260px] border-l bg-white shrink-0 flex flex-col overflow-hidden">
                <DynamicFieldsSidebar placeholders={placeholders} onInsert={insertPlaceholder} />
              </div>
            </div>

            {/* Bottom bar */}
            <div className="px-5 py-3 border-t flex justify-between gap-2 shrink-0 bg-gray-50">
              <Button type="button" variant="outline" size="sm" onClick={handleSaveHeaderFooter}
                className="gap-1.5 text-teal-700 border-teal-300 hover:bg-teal-50"
                data-testid="save-header-footer-btn"
                title={!editingTemplate ? 'Önce şablonu oluşturun' : 'Sadece Header/Footer ayarlarını kaydet'}>
                <Save className="w-3.5 h-3.5" /> Header/Footer Kaydet
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={closeForm}>İptal</Button>
                <Button type="button" size="sm" onClick={handleSubmit} data-testid="save-template-btn">
                  {editingTemplate ? 'Güncelle' : 'Oluştur'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Şablonu Sil</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.name}</strong> şablonunu silmek istediğinizden emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
